import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
} from '@mui/material';
import { tokens } from '../../theme';
import TableCell from './TableCell';
import { defaultTableConfig, initializeStudentAnswer, updateStudentAnswer } from './tableUtils';

/**
 * TableStudent Component
 * Student interface for answering table-based homework questions.
 * Displays prefilled cells as read-only and allows input for editable cells.
 */
const TableStudent = ({
  config = defaultTableConfig,
  value = null,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Initialize answer from value or config
  const [answer, setAnswer] = useState(() => {
    if (value && value.rows && value.rows.length > 0) {
      return value;
    }
    return initializeStudentAnswer(config);
  });

  // Sync with external value changes
  useEffect(() => {
    if (value && value.rows && value.rows.length > 0) {
      setAnswer(value);
    } else if (!value || !value.rows || value.rows.length === 0) {
      // Initialize from config if value is empty
      setAnswer(initializeStudentAnswer(config));
    }
  }, [value, config]);

  // Handle cell value change
  const handleCellChange = (rowId, colId, cellValue) => {
    const newAnswer = updateStudentAnswer(answer, rowId, colId, cellValue);
    setAnswer(newAnswer);
    onChange?.(newAnswer);
  };

  const getColumnType = (columnId) => {
    return config.columns?.find(c => c.id === columnId)?.type || 'math';
  };

  const getCellValue = (rowId, colId) => {
    const row = answer.rows?.find(r => r.id === rowId);
    return row?.cells?.[colId] || '';
  };

  const isCellPrefilled = (rowId, colId) => {
    const configRow = config.rows?.find(r => r.id === rowId);
    return configRow?.cells?.[colId]?.prefilled || false;
  };

  const getPrefilledValue = (rowId, colId) => {
    const configRow = config.rows?.find(r => r.id === rowId);
    return configRow?.cells?.[colId]?.value || '';
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600] }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {config.columns?.map((col) => (
                <MuiTableCell
                  key={col.id}
                  sx={{
                    fontWeight: 'bold',
                    minWidth: 100,
                    backgroundColor: colors.primary[500],
                    color: '#fff',
                  }}
                >
                  {col.type === 'math' ? (
                    <TableCell
                      value={col.header}
                      type="math"
                      readOnly
                      size="small"
                    />
                  ) : (
                    col.header || '-'
                  )}
                </MuiTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {config.rows?.map((configRow) => (
              <TableRow key={configRow.id}>
                {config.columns?.map((col) => {
                  const prefilled = isCellPrefilled(configRow.id, col.id);
                  const displayValue = prefilled
                    ? getPrefilledValue(configRow.id, col.id)
                    : getCellValue(configRow.id, col.id);

                  return (
                    <MuiTableCell
                      key={col.id}
                      sx={{
                        backgroundColor: prefilled
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(0,0,0,0.03)'
                          : 'transparent',
                        padding: '8px',
                      }}
                    >
                      <TableCell
                        value={displayValue}
                        onChange={
                          prefilled || disabled
                            ? undefined
                            : (val) => handleCellChange(configRow.id, col.id, val)
                        }
                        type={getColumnType(col.id)}
                        readOnly={prefilled}
                        disabled={disabled && !prefilled}
                        size="small"
                        sx={{ width: '100%' }}
                      />
                    </MuiTableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TableStudent;
